import { Injectable } from '@angular/core';
import { GlobalConstants } from '../constant/constant';
import { StoreName } from '../entities/StoreName';
import { DbKeyType } from '../models/db/db-store';
import { DbTransaction, IndexedDbTransaction } from '../models/db/db-transaction';
import { SyncMasterService } from './syncMaster.service';
import Dexie from 'dexie';
import { exportDB } from 'dexie-export-import';
import { FormDocumentMetadata } from '../models/form-document/form-document-metadata';
import { traverse } from 'object-traversal';
import FormDocument from '../models/form-document/form-document';
import { ProjectGIS } from '../entities/ProjectGIS';
@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private db: IDBDatabase;

  /** The current version of the database. Increment this number to trigger an upgrade. */
  private DB_VERSION = 14;

  /**
   * Defines the upgrade logic for each version. The key is the version number and the value is a function that takes an IDBVersionChangeEvent and performs the upgrade.
   * DO NOT CHANGE EXISTING VERSIONS IF THEY HAVE ALREADY BEEN DEPLOYED TO PRODUCTION.
   */
  private DB_VERSION_UPGRADES: { [key: number]: (event: IDBVersionChangeEvent) => void } = {
    2: event => {
      // Version 2: Create object stores for syncMasterTables
      this.createSyncMasterTables(event);
    },
    3: event => {
      // Version 3: Create "id" index for each syncMasterTable
      const syncMasterTables = this.syncMasterService.getSyncMasterTables();
      for (const syncMasterTable of syncMasterTables) {
        const storeName = syncMasterTable.tableName.toLowerCase();
        this.createIndex(event, storeName, GlobalConstants.KEY_NAMES.Id, syncMasterTable.idIndexKeyPath, false);
      }
    },
    4: event => {
      // Version 4: Create ActionSync store
      this.createStore(event, StoreName.SyncActionQueue, GlobalConstants.DEFAULT_PRIMARY_KEY, true);
      this.createIndex(event, StoreName.SyncActionQueue, GlobalConstants.KEY_NAMES.Status, 'status', false);
    },
    5: event => {
      // Version 5: Create indexes for foreign keys and rename Activity's TimeRecordId to ReferenceRecordId
      this.renameKeyPath(event, StoreName.Activity, 'TimeRecordId', 'ReferenceRecordId');
      this.createIndex(event, StoreName.ActivityLog, GlobalConstants.KEY_NAMES.UserId, 'UserId', false);
      this.createIndex(event, StoreName.ActivityLog, GlobalConstants.KEY_NAMES.ProjectId, 'ProjectId', false);
      this.createIndex(event, StoreName.TimeRecord, GlobalConstants.KEY_NAMES.ActivityLogId, 'ActivityLogId', false);
      this.createIndex(event, StoreName.TimeRecord, GlobalConstants.KEY_NAMES.ActivityLogRecordId, 'ActivityLogRecordId', false);
      this.createIndex(event, StoreName.TimeRecord, GlobalConstants.KEY_NAMES.UserId, 'EmployeeId', false);
      this.createIndex(event, StoreName.Activity, GlobalConstants.KEY_NAMES.TimeRecordId, 'ReferenceId', false);
      this.createIndex(event, StoreName.Activity, GlobalConstants.KEY_NAMES.TimeRecordRecordId, 'ReferenceRecordId', false);
      this.createIndex(event, StoreName.Activity, GlobalConstants.KEY_NAMES.ActivityLogId, 'ActivityLogId', false);
      this.createIndex(event, StoreName.Activity, GlobalConstants.KEY_NAMES.ActivityLogRecordId, 'ActivityLogRecordId', false);
      this.createIndex(event, StoreName.Activity, GlobalConstants.KEY_NAMES.UserId, 'UserId', false);
      this.createIndex(event, StoreName.NotableActivityOption, GlobalConstants.KEY_NAMES.ProjectId, 'ProjectId', false);
      this.createIndex(event, StoreName.NotableActivityOption, GlobalConstants.KEY_NAMES.LaborCategoryId, 'LaborCategoryId', false);
      this.createIndex(event, StoreName.LaborCategory, GlobalConstants.KEY_NAMES.ProjectId, 'ProjectId', false);
      this.createIndex(event, StoreName.Task, GlobalConstants.KEY_NAMES.ProjectId, 'ProjectId', false);
      this.createIndex(event, StoreName.Users2FieldActivity, GlobalConstants.KEY_NAMES.UserId, 'UserId', false);
      this.createIndex(event, StoreName.Users2FieldActivity, GlobalConstants.KEY_NAMES.FieldActivityId, 'FieldActivityId', false);
      this.createIndex(event, StoreName.Users2Project, GlobalConstants.KEY_NAMES.UserId, 'UserId', false);
      this.createIndex(event, StoreName.Team2User, GlobalConstants.KEY_NAMES.TeamId, 'TeamId', false);
      this.createIndex(event, StoreName.TimeManager2Project, GlobalConstants.KEY_NAMES.UserId, 'ManagerId', false);
      this.createIndex(event, StoreName.TimeManager2Team, GlobalConstants.KEY_NAMES.UserId, 'ManagerId', false);
    },
    6: event => {
      // Version 6: Create composite indexes
      this.createIndex(event, StoreName.Activity, GlobalConstants.KEY_NAMES.UserId_Date, ['UserId', 'UserSpecifiedDate'], false);
      this.createIndex(event, StoreName.ActivityLog, GlobalConstants.KEY_NAMES.UserId_Date, ['UserId', 'ActivityDate'], false);
      this.createIndex(event, StoreName.ActivityLog, GlobalConstants.KEY_NAMES.UserId_ProjectId_Date, ['UserId', 'ProjectId', 'ActivityDate'], false);
      this.createIndex(event, StoreName.TimeRecord, GlobalConstants.KEY_NAMES.UserId_Date, ['EmployeeId', 'ClockIn'], false);
      this.createIndex(event, StoreName.TimeRecord, GlobalConstants.KEY_NAMES.UserId_ProjectId_Date, ['EmployeeId', 'ProjectId', 'ClockIn'], false);
    },
    7: event => {
      // Version 7: Create UserSyncData store
      this.createStore(event, StoreName.SyncParams, GlobalConstants.DEFAULT_PRIMARY_KEY, true);
      this.createIndex(event, StoreName.SyncParams, GlobalConstants.KEY_NAMES.SyncType_Id, ['Type', 'Id'], true);
    },
    8: event => {
      // Version 8: Create FieldActivity store
      this.createStore(event, StoreName.FieldActivity, GlobalConstants.DEFAULT_PRIMARY_KEY, true);
      this.createIndex(event, StoreName.FieldActivity, GlobalConstants.KEY_NAMES.Id, 'Id', false);
      this.createIndex(event, StoreName.FieldActivity, GlobalConstants.KEY_NAMES.ProjectId, 'ProjectId', false);
    },
    9: event => {
      // Version 9: Create FormDocuments and FormDefinitions store indexes
      this.createIndex(event, StoreName.FormDocuments, GlobalConstants.KEY_NAMES.DocumentId, 'DocumentId', true);
      this.createIndex(event, StoreName.FormDocuments, GlobalConstants.KEY_NAMES.FormDefinitionBaseId, 'FormDefinitionBaseId', false);
      this.createIndex(event, StoreName.FormDocuments, GlobalConstants.KEY_NAMES.SyncStatus, 'syncStatus', false);
      this.createIndex(event, StoreName.FormDocuments, GlobalConstants.KEY_NAMES.ModifiedDate, 'ModifiedDate', false);
      this.createIndex(event, StoreName.FormDocuments, GlobalConstants.KEY_NAMES.SyncStatus_ModifiedDate, ['syncStatus', 'ModifiedDate'], false);
      this.createIndex(event, StoreName.FormDefinition, GlobalConstants.KEY_NAMES.FormDefinitionBaseId, 'baseFormDefinitionId', false);
      this.createIndex(event, StoreName.FormDefinition, GlobalConstants.KEY_NAMES.ProjectId, 'projectId', false);
    },
    10: event => {
      // Version 10: Create ChoiceDefinitions store indexes
      this.createIndex(event, StoreName.ChoiceDefinition, GlobalConstants.KEY_NAMES.Id, 'id', true);
    },
    11: event => {
      // Version 11: Remove all blobs from existing form documents
      this.migrateFormDocuments();
    },
    12: event => {
      // Version 12: Create FormDocumentMedia store
      this.createStore(event, StoreName.FormDocumentMedia, GlobalConstants.DEFAULT_PRIMARY_KEY, true);
      // NOTE: Setting RecordId as a custom index to optimize batch retrievals for form document media, addressing the limitations of IndexedDB that prevent efficient querying of multiple records by primary key in a single operation.
      this.createIndex(event, StoreName.FormDocumentMedia, GlobalConstants.KEY_NAMES.RecordId, 'RecordId', true);
      this.createIndex(event, StoreName.FormDocumentMedia, GlobalConstants.KEY_NAMES.DocumentId, 'DocumentId', false);
      this.migrateFormDocumentsMedia();
    },
    13: event => {
      // Version 13: Create OdometerReading store and indexes
      this.createStore(event, StoreName.OdometerReading, GlobalConstants.DEFAULT_PRIMARY_KEY, true);
      this.createIndex(event, StoreName.OdometerReading, GlobalConstants.KEY_NAMES.Id, 'Id', false);
      this.createIndex(event, StoreName.OdometerReading, GlobalConstants.KEY_NAMES.ActivityLogId, 'ActivityLogId', false);
      this.createIndex(event, StoreName.OdometerReading, GlobalConstants.KEY_NAMES.ActivityLogRecordId, 'ActivityLogRecordId', false);
      this.createIndex(event, StoreName.OdometerReading, GlobalConstants.KEY_NAMES.UserId, 'UserId', false);
      this.createIndex(event, StoreName.OdometerReading, GlobalConstants.KEY_NAMES.UserId_Date, ['UserId', 'Date'], false);
    },
    14: event => {
      // Version 14: Add odometerReadingMedia store and indexes
      this.createStore(event, StoreName.OdometerReadingMedia, GlobalConstants.DEFAULT_PRIMARY_KEY, true);
      this.createIndex(event, StoreName.OdometerReadingMedia, GlobalConstants.KEY_NAMES.Id, 'Id', false);
      this.createIndex(event, StoreName.OdometerReadingMedia, GlobalConstants.KEY_NAMES.OdomterReadingRecordId, 'OdometerReadingRecordId', false);
    }
    // TODO: ProjectGIS - Temporarily disabled pending server-side fetch implementation
    // 15: event => {
    //   // Version 15: Create ProjectGIS store and indexes
    //   this.createStore(event, StoreName.ProjectGIS, GlobalConstants.DEFAULT_PRIMARY_KEY, true);
    //   this.createIndex(event, StoreName.ProjectGIS, GlobalConstants.KEY_NAMES.Id, 'Id', true);
    //   this.createIndex(event, StoreName.ProjectGIS, GlobalConstants.KEY_NAMES.ProjectId, 'ProjectId', true);
    // }
  };

  constructor(private syncMasterService: SyncMasterService) {
    this.initDb().catch(e => console.log('Error initializing db'));
  }

  dropDatabase(): Promise<void> {
    return new Promise(async resolve => {
      //await this.clearObjectStores();
      if (this.db) {
        this.db.close();
      }
      this.db = undefined;

      const applicationUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
      //console.log('calling delete database in dropDatabase()');
      const dbName = GlobalConstants.dbName + '_' + applicationUser.Id;
      const deleteRequest = indexedDB.deleteDatabase(dbName);
      deleteRequest.onblocked = ev => {
        //console.log('dropDatabase() blocked event');
      };
      deleteRequest.onerror = ev => {
        //console.log('dropDatabase() error event');
      };
      deleteRequest.onsuccess = ev => {
        this.clearDBInstance();
        //console.log('delete database success event');
        return resolve();
      };
      deleteRequest.onupgradeneeded = async ev => {
        //console.log('deleteRequest.onupgradeneeded');
      };
    });
  }

  async createData(item: any, storeName: string, transaction: DbTransaction): Promise<number> {
    await this.initDb();

    const store = transaction.objectStore(storeName);
    const recordId = await store.create(item).catch((e: any) => {
      throw new Error(`Error creating record in table '${storeName}'. Error code: ${e.code}, message: ${e.message}. Item: ${JSON.stringify(item)}`);
    });

    return recordId;
  }

  insertBulkData(item: any, storeName: string): Promise<number> {
    storeName = storeName.toLowerCase();
    return new Promise(resolve =>
      this.initDb().then(() => {
        let createdId: number;
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        item.map(i => {
          store.put(i);
        });
        tx.oncomplete = async () => resolve(0);
      })
    );
  }

  getCount(storeName: string): Promise<number> {
    storeName = storeName.toLowerCase();
    return new Promise(resolve =>
      this.initDb().then(() => {
        let countOfItems: number;
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.count();
        request.onsuccess = (e: any) => {
          countOfItems = e.target.result;
        };
        tx.oncomplete = () => resolve(countOfItems);
      })
    );
  }

  async updateData(item: any, storeName: string, transaction: DbTransaction): Promise<number> {
    await this.initDb();

    const store = transaction.objectStore(storeName);
    const recordId = await store.update(item).catch((e: any) => {
      throw new Error(`Error updating recordId in table '${storeName}'. Error code: ${e.code}, message: ${e.message}. Item: ${JSON.stringify(item)}`);
    });

    return recordId;
  }

  deleteAll(storeName: string): Promise<void> {
    storeName = storeName.toLowerCase();
    return new Promise(resolve =>
      this.initDb().then(() => {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        store.clear();
        tx.oncomplete = () => {
          localStorage.setItem('lastUpdate', Date.now().toString());
          return resolve();
        };
      })
    );
  }

  deleteBulkData(item: any, storeName: string): Promise<number> {
    storeName = storeName.toLowerCase();
    return new Promise(resolve =>
      this.initDb().then(() => {
        const tx = this.db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        item.map(i => {
          store.delete(i.RecordId);
        });
        tx.oncomplete = async () => resolve(0);
      })
    );
  }

  async deleteData(item: any, storeName: string, transaction: DbTransaction): Promise<void> {
    await this.initDb();

    const store = transaction.objectStore(storeName);
    await store.delete(item).catch((e: any) => {
      throw new Error(`Error deleting record in table '${storeName}'. Error code: ${e.code}, message: ${e.message}. Item: ${JSON.stringify(item)}`);
    });

    return;
  }

  async getAllPartialFields<T>(storeName: string, T: new (record: any) => T, transaction: DbTransaction): Promise<T[]> {
    await this.initDb();
    const store = transaction.objectStore(storeName);

    return await store.getAllPartialFields(T);
  }

  async getAll(storeName: string, transaction: DbTransaction): Promise<any> {
    await this.initDb();
    const store = transaction.objectStore(storeName);
    const records = await store.getAll().catch((e: any) => {
      throw new Error(`Error getting all records in table '${storeName}'. Error code: ${e.code}, message: ${e.message}.}`);
    });

    return records;
  }

  async getOne(storeName: string, primaryKey: number, transaction: DbTransaction): Promise<any> {
    await this.initDb();

    const store = transaction.objectStore(storeName);
    const record = await store.get(primaryKey).catch((e: any) => {
      throw new Error(`Error getting record with primary key '${primaryKey}' in table '${storeName}'. Error code: ${e.code}, message: ${e.message}.}`);
    });

    return record;
  }

  async getOnePartialFieldsByIndex<T>(storeName: string, indexName: string, indexKey: DbKeyType, T: new (record: any) => T, transaction: DbTransaction): Promise<any> {
    await this.initDb();

    const store = transaction.objectStore(storeName);
    const record = await store.getPartialFieldsByIndex(indexName, indexKey, T).catch((e: any) => {
      throw new Error(`Record not found on table '${storeName}' using index '${indexName}' for key '${indexKey}'. Error code: ${e.code}, message: ${e.message}`);
    });

    return record;
  }

  async getOneByIndex(storeName: string, indexName: string, indexKey: DbKeyType, transaction: DbTransaction): Promise<any> {
    await this.initDb();

    const store = transaction.objectStore(storeName);
    const record = await store.getByIndex(indexName, indexKey).catch((e: any) => {
      throw new Error(`Record not found on table '${storeName}' using index '${indexName}' for key '${indexKey}'. Error code: ${e.code}, message: ${e.message}`);
    });

    return record;
  }

  async getAllPartialFieldsByIndex<T>(storeName: string, indexName: string, indexKey: DbKeyType, T: new (record: any) => T, transaction: DbTransaction): Promise<T[]> {
    await this.initDb();
    const store = transaction.objectStore(storeName);

    return await store.getAllPartialFieldsByIndex(indexName, indexKey, T);
  }

  async getAllByIndex(storeName: string, indexName: string, indexKey: DbKeyType, transaction: DbTransaction): Promise<any> {
    await this.initDb();

    const store = transaction.objectStore(storeName);

    const records = await store.getAllByIndex(indexName, indexKey).catch((e: any) => {
      throw new Error(`Error getting all records in table '${storeName}' using index '${indexName}' for key '${indexKey}'. Error code: ${e.code}, message: ${e.message}`);
    });

    return records;
  }

  private checkDB() {
    const applicationUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
    if (this.db && +this.db.name.split('_')[1] !== +applicationUser.Id) {
      console.log(`db name mismatch with app user id. db name: ${this.db && this.db.name}, userid: ${applicationUser.Id}`);
      this.db.close();
      return false;
    }
    return true;
  }

  private clearDBInstance() {
    if (this.db && this.checkDB() === true) {
      this.db = undefined;
    }
  }

  initDb(): Promise<void> {
    if (this.db && this.checkDB() === true) {
      return Promise.resolve();
    }
    return this.openDb().catch(e => {
      console.log('Error opening db');
      return Promise.reject();
    });
  }

  private openDb(): Promise<void> {
    return new Promise((resolve, reject) => {
      //console.log('calling openDB');
      const applicationUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
      if (!applicationUser) {
        console.log('application user missing');
        return reject();
      }
      const dbName = GlobalConstants.dbName + '_' + applicationUser.Id;
      const openRequest = indexedDB.open(dbName, this.DB_VERSION);
      //console.log(openRequest);
      openRequest.onupgradeneeded = async event => {
        for (const [version, upgrade] of Object.entries(this.DB_VERSION_UPGRADES)) {
          const versionNumber = Number(version);
          if (event.oldVersion < versionNumber && event.newVersion >= versionNumber) {
            await upgrade(event);
          }
        }
      };

      openRequest.onsuccess = event => {
        //console.log('indexed db open request success event');
        this.db = (event.target as any).result;

        this.db.onerror = e => {
          console.log(e);
          return resolve();
        };

        this.db.onversionchange = e => {
          //console.log('version change event triggered in storage service');
          //console.log(e);
          (e.target as any).close();
          if (this.db) {
            this.db.close();
          }
          return resolve();
        };

        return resolve();
      };
      openRequest.onblocked = e => {
        //console.log('closing db connection as it is blocked');
        if (this.db) {
          this.db.close();
        }
        //return resolve();
      };
    });
  }

  private createSyncMasterTables(event: IDBVersionChangeEvent) {
    //console.log('creating object store');
    const target: EventTarget | null = event.target;
    const db = (target as any).result;

    const storeSync = db.createObjectStore(StoreName.SyncMaster.toLowerCase(), {
      keyPath: 'Id',
      autoIncrement: true,
    });
    storeSync.createIndex(StoreName.SyncMaster, 'Id', { unique: true });

    this.syncMasterService.getSyncMasterTables().map(element => {
      if (!db.objectStoreNames.contains(element.tableName.toLowerCase())) {
        db.createObjectStore(element.tableName.toLowerCase(), {
          keyPath: element.primaryKeyPath,
          autoIncrement: true,
        });
      }
    });
  }

  private createStore(event: IDBVersionChangeEvent, storeName: string, keyPath: string, autoIncrement: boolean): void {
    const target: IDBOpenDBRequest = event.target as IDBOpenDBRequest;
    const db = target.result;
    if (!db.objectStoreNames.contains(storeName)) {
      db.createObjectStore(storeName, { keyPath: keyPath, autoIncrement: autoIncrement });
    }
  }

  private createIndex(event: IDBVersionChangeEvent, storeName: string, indexName: string, keyPath: string | string[], isUnique: boolean): void {
    const request: any = event.target;
    const txn = request.transaction;
    const store = txn.objectStore(storeName);
    if (!store.indexNames.contains(indexName)) {
      store.createIndex(indexName, keyPath, { unique: isUnique });
    }
  }

  private renameStore(event: IDBVersionChangeEvent, oldStoreName: string, newStoreName: string): void {
    const request: any = event.target;
    const db = request.result;
    const txn = request.transaction;

    if (!db.objectStoreNames.contains(oldStoreName)) {
      console.error(`Store ${oldStoreName} not found.`);
      return;
    }

    // Create a new store with the same keyPath and indexes as the old store
    const oldStore = txn.objectStore(oldStoreName);
    const newStore = db.createObjectStore(newStoreName, { keyPath: oldStore.keyPath });

    // Copy indexes from old store to new store
    Array.from(oldStore.indexNames).forEach(indexName => {
      const index = oldStore.index(indexName);
      newStore.createIndex(indexName, index.keyPath, { unique: index.unique, multiEntry: index.multiEntry });
    });

    // Copy all data from old to new store
    oldStore.openCursor().onsuccess = function (event) {
      const cursor = event.target.result;
      if (cursor) {
        newStore.add(cursor.value);
        cursor.continue();
      } else {
        // After copying all data, delete the old store
        db.deleteObjectStore(oldStoreName);
      }
    };
  }

  private renameKeyPath(event: IDBVersionChangeEvent, storeName: string, oldKeyPath: string, newKeyPath: string): void {
    const request: any = event.target;
    const txn = request.transaction;
    const store = txn.objectStore(storeName);

    const getAllRequest = store.getAll();

    getAllRequest.onsuccess = () => {
      const records = getAllRequest.result;

      records.forEach(record => {
        if (oldKeyPath in record) {
          record[newKeyPath] = record[oldKeyPath];
          delete record[oldKeyPath];

          const putRequest = store.put(record);

          putRequest.onsuccess = () => {
            console.log('Record updated successfully');
          };

          putRequest.onerror = () => {
            console.error('Error updating record:', putRequest.error);
          };
        }
      });
    };

    getAllRequest.onerror = () => {
      console.error('Error getting records:', getAllRequest.error);
    };
  }

  private async deleteObjectStores(event: IDBVersionChangeEvent) {
    //console.log('deleting object store');
    const db = event.target as any;
    const objectNames: string[] = [];

    if (db) {
      //console.log('db is available');
      const listofStoreNames = db.objectStoreNames;
      for (let i = 0; i < listofStoreNames.length; i++) {
        //console.log(listofStoreNmaes.item(i));
        objectNames.push(listofStoreNames.item(i));
      }

      await objectNames.reduce(async (promise, element) => {
        await promise;
        db.deleteObjectStore(element);
      }, Promise.resolve());
    }
  }

  private async clearObjectStores() {
    //console.log('clearObjectStores');
    const objectNames: string[] = [];
    if (this.db) {
      const listofStoreNmaes = this.db.objectStoreNames;
      for (let i = 0; i < listofStoreNmaes.length; i++) {
        //console.log(listofStoreNmaes.item(i));
        objectNames.push(listofStoreNmaes.item(i));
      }

      await objectNames.reduce(async (promise, element) => {
        await promise;
        await this.clearData(element).catch(() => {});
      }, Promise.resolve());
    }
  }

  private async clearData(storeName: string): Promise<void> {
    storeName = storeName.toLowerCase();
    return new Promise(resolve =>
      this.initDb().then(() => {
        try {
          const tx = this.db.transaction(storeName, 'readwrite');
          const store = tx.objectStore(storeName);
          store.clear();
          tx.oncomplete = () => resolve();
        } catch (e) {
          console.log(e);
          return resolve();
        }
      })
    );
  }

  async exportDatabase(tables: StoreName[] = [], onProgress?: (progress: string) => void): Promise<Blob> {
    const applicationUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY));
    if (!applicationUser) {
      console.log('application user missing');
      throw new Error('Application user missing');
    }
    const dbName = GlobalConstants.dbName + '_' + applicationUser.Id;

    const skippedTables = [];

    if (tables.length > 0) {
      skippedTables.push(
        ...Object.keys(StoreName)
          .map(key => StoreName[key])
          .filter(table => !tables.includes(table))
      );
    }

    const db = await new Dexie(dbName).open();
    const blob = await exportDB(db, {
      prettyJson: true,
      skipTables: skippedTables,
      numRowsPerChunk: 1,
      progressCallback: progress => {
        if (onProgress) {
          const percentage = Math.round((progress.completedRows / progress.totalRows) * 100);
          onProgress(`Exporting database... ${percentage}%`);
          return true;
        }
        return true;
      },
    });
    return blob;
  }

  private async migrateFormDocuments() {
    const transaction = await this.startTransaction([StoreName.FormDocuments], 'readwrite');
    const formDocumentsMetadata = await this.getAllPartialFields(StoreName.FormDocuments, FormDocumentMetadata, transaction);
    for (const metadata of formDocumentsMetadata) {
      const formDocument: FormDocument = await this.getOne(StoreName.FormDocuments, metadata.RecordId, transaction);

      traverse(formDocument.FormComponents, ({ value }) => {
        if (value && typeof value === 'object' && value.image) {
          delete value.image;
        }
      });
      await this.updateData(formDocument, StoreName.FormDocuments, transaction);
    }
  }

  private async migrateFormDocumentsMedia() {
    const transaction = await this.startTransaction([StoreName.FormDocuments, StoreName.FormDocumentMedia], 'readwrite');
    const formDocumentsMetadata = await this.getAllPartialFields(StoreName.FormDocuments, FormDocumentMetadata, transaction);
    for (const metadata of formDocumentsMetadata) {
      const formDocument: FormDocument = await this.getOne(StoreName.FormDocuments, metadata.RecordId, transaction);

      const mediaRecordsPromises = [];
      traverse(formDocument.FormComponents, async ({ value }) => {
        if (value && typeof value === 'object' && value.dataUrl) {
          mediaRecordsPromises.push(
            new Promise(async (resolve, reject) => {
              try {
                const mediaRecordId = await this.createData(
                  {
                    DocumentId: metadata.DocumentId,
                    Value: value.dataUrl,
                  },
                  StoreName.FormDocumentMedia,
                  transaction
                );

                delete value.dataUrl;
                delete value.Id;
                value.formDocumentMediaRecordId = mediaRecordId;
                resolve(true);
              } catch (err) {
                reject(err);
              }
            })
          );
        }
      });
      await Promise.all(mediaRecordsPromises);
      await this.updateData(formDocument, StoreName.FormDocuments, transaction);
    }
  }

  /**
   * Starts a new transaction with the specified store names and mode.
   * @param storeNames - An array of store names to include in the transaction.
   * @param mode - The mode of the transaction (e.g., "readonly", "readwrite", "versionchange").
   * @returns A new instance of the DbTransaction interface representing the transaction.
   */
  async startTransaction(storeNames: string[], mode: IDBTransactionMode): Promise<DbTransaction> {
    await this.initDb();

    storeNames = storeNames.map(storeName => storeName.toLowerCase());
    const tx = this.db.transaction(storeNames, mode);
    return new IndexedDbTransaction(tx);
  }
}
